import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import HomePage from './Pages/HomePage';
import ComingSoon from './Pages/ComingSoon';
import Shop from './Pages/Shop';
import AuthPage from './Pages/AuthPage';
import ForgotPassword from './Pages/ForgotPassword';
import Checkout from './Pages/Checkout';
import CustomizeOutfit from './Pages/CustomizeOutfit';
import ProductDetail from './Pages/ProductDetail';
import UserDashboard from './Pages/UserDashboard';
import AdminDashboard from './Pages/AdminDashboard';
import NotFound from './Pages/NotFound';
import CartSidebar from './components/CartSidebar';
import ErrorBoundary, { PageErrorBoundary } from './components/ErrorBoundary';
import AppProviders from './components/AppProviders';
import errorHandler from './services/errorHandler';
import { useCart } from './hooks/useCart';
import { useAuth } from './contexts/AuthContext';
import './styles/ErrorBoundary.css';

const RequireAuth = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to='/auth?mode=login' state={{ returnTo: location.pathname }} replace />;
  }

  return children;
};

const AppShell = () => {
  const {
    cartItems,
    isCartOpen,
    cartItemCount,
    addToCart,
    addMultipleToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    toggleCart,
    closeCart,
  } = useCart();

  useEffect(() => {
    const handleGlobalError = error => {
      errorHandler.showUserFriendlyMessage(error);
    };

    errorHandler.addErrorListener(handleGlobalError);

    const handleUnhandledRejection = event => {
      errorHandler.logError(new Error(event.reason), { type: 'unhandled_rejection' });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      errorHandler.removeErrorListener(handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const homePage = (
    <PageErrorBoundary pageName='Home'>
      <HomePage />
    </PageErrorBoundary>
  );
  const comingSoonPage = (
    <PageErrorBoundary pageName='ComingSoon'>
      <ComingSoon />
    </PageErrorBoundary>
  );
  const shopPage = (
    <PageErrorBoundary pageName='Shop'>
      <Shop />
    </PageErrorBoundary>
  );
  const productPage = (
    <PageErrorBoundary pageName='Product'>
      <ProductDetail onAddToCart={addToCart} />
    </PageErrorBoundary>
  );
  const authPage = (
    <PageErrorBoundary pageName='Auth'>
      <AuthPage />
    </PageErrorBoundary>
  );
  const forgotPasswordPage = (
    <PageErrorBoundary pageName='ForgotPassword'>
      <ForgotPassword />
    </PageErrorBoundary>
  );
  const checkoutPage = (
    <PageErrorBoundary pageName='Checkout'>
      <Checkout cartItems={cartItems} onOrderPlaced={clearCart} />
    </PageErrorBoundary>
  );
  const customizePage = (
    <PageErrorBoundary pageName='CustomizeOutfit'>
      <CustomizeOutfit onAddToCart={addMultipleToCart} />
    </PageErrorBoundary>
  );
  const accountPage = (
    <PageErrorBoundary pageName='Account'>
      <RequireAuth>
        <UserDashboard onAddToCart={addMultipleToCart} />
      </RequireAuth>
    </PageErrorBoundary>
  );
  const adminPage = (
    <PageErrorBoundary pageName='Admin'>
      <AdminDashboard />
    </PageErrorBoundary>
  );
  const notFoundPage = (
    <PageErrorBoundary pageName='NotFound'>
      <NotFound />
    </PageErrorBoundary>
  );

  return (
    <Router>
      <div className='App'>
        <Navbar toggleCart={toggleCart} cartItemCount={cartItemCount} />
        <CartSidebar
          isOpen={isCartOpen}
          cartItems={cartItems}
          onClose={closeCart}
          onUpdateQuantity={updateQuantity}
          onRemoveItem={removeFromCart}
        />
        <ErrorBoundary name='App-Routes'>
          <Routes>
            <Route path='/' element={homePage} />
            <Route path='/coming-soon' element={comingSoonPage} />
            <Route path='/shop' element={shopPage} />
            <Route path='/product/:productId' element={productPage} />
            <Route path='/auth' element={authPage} />
            <Route path='/forgot-password' element={forgotPasswordPage} />
            <Route path='/checkout' element={checkoutPage} />
            <Route path='/customize-outfit' element={customizePage} />
            <Route path='/account' element={accountPage} />
            <Route path='/dashboard' element={<Navigate to='/account' replace />} />
            <Route path='/profile' element={<Navigate to='/account?tab=account' replace />} />
            <Route path='/orders' element={<Navigate to='/account?tab=orders' replace />} />
            <Route path='/admin' element={adminPage} />
            <Route path='*' element={notFoundPage} />
          </Routes>
        </ErrorBoundary>
        <footer className='site-footer'>
          <p>&copy; {new Date().getFullYear()} Amiras Fashion. All rights reserved.</p>
        </footer>
      </div>
    </Router>
  );
};

const App = () => (
  <AppProviders>
    <AppShell />
  </AppProviders>
);

export default App;
